import type { FC, ReactNode } from 'react'
import type { PlanId } from '@/types'

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'

interface Plan {
    id: PlanId
    name: string
    summary: string
    price: string
    cadence?: string
    cta: string
    featured?: boolean
    features: Array<string>
}

const PLANS: Array<Plan> = [
    {
        id: 'agent',
        name: 'Agent',
        summary: 'For solo agents with a few deals in motion at once.',
        price: '$59',
        cadence: 'per month',
        cta: 'Request access',
        features: [
            'Up to 3 active deals',
            'Contract to timeline in minutes',
            'Deadline and signature reminders',
            'Client updates by email and text'
        ]
    },
    {
        id: 'team',
        name: 'Team',
        summary: 'For teams that open and close deals every single week.',
        price: '$189',
        cadence: 'per month',
        cta: 'Request access',
        featured: true,
        features: [
            'Up to 15 active deals',
            'Everything in Agent',
            'Shared team calendar and inbox',
            'Approval queue for outgoing messages',
            'Priority support'
        ]
    },
    {
        id: 'brokerage',
        name: 'Brokerage',
        summary: 'For brokerages with a back office and broker review.',
        price: 'Custom',
        cta: 'Talk to us',
        features: [
            'Unlimited active deals',
            'Everything in Team',
            'Compliance checklists for each office',
            'Broker review queue',
            'Guided onboarding for every office'
        ]
    }
]

export const PlanGrid: FC = (): ReactNode => {
    return (
        <div className='grid gap-4 md:grid-cols-3 lg:gap-5'>
            {PLANS.map((plan, index) => (
                <div
                    key={plan.id}
                    data-reveal='scale'
                    className={cn('reveal animate-in duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-backwards fade-in slide-in-from-bottom-10', `reveal-d${index + 1}`)}
                    style={{ animationDelay: `${450 + index * 150}ms` }}
                >
                    <article
                        aria-labelledby={`plan-${plan.id}`}
                        className={cn(
                            'flex h-full flex-col rounded-2xl border bg-paper/95 p-6 transition-[translate,box-shadow] duration-500 ease-out hover:-translate-y-2 motion-reduce:hover:translate-y-0 sm:p-7',
                            plan.featured
                                ? 'border-primary/30 shadow-[0_40px_80px_-40px_rgba(38,56,92,0.65)] ring-1 ring-primary/15 hover:shadow-[0_50px_90px_-40px_rgba(38,56,92,0.75)]'
                                : 'border-white/70 shadow-[0_24px_60px_-40px_rgba(38,56,92,0.5)] hover:shadow-[0_40px_80px_-40px_rgba(38,56,92,0.6)]'
                        )}
                    >
                        <div className='flex items-center justify-between gap-3'>
                            <h3 id={`plan-${plan.id}`} className='text-[17px] font-semibold text-foreground'>
                                {plan.name}
                            </h3>

                            {plan.featured && (
                                <span className='sheen relative overflow-hidden rounded-full bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground'>
                                    Most popular
                                </span>
                            )}
                        </div>

                        <p className='mt-2 min-h-[2.75rem] text-sm leading-relaxed text-muted-foreground'>
                            {plan.summary}
                        </p>

                        <p className='mt-6 flex items-baseline gap-2'>
                            <span className='font-heading text-[3.5rem] leading-none tracking-[-0.03em] text-foreground'>
                                {plan.price}
                            </span>

                            {plan.cadence && (
                                <span className='text-sm text-mist'>
                                    {plan.cadence}
                                </span>
                            )}
                        </p>

                        <ButtonLink
                            href={`/contact?topic=pricing&plan=${plan.id}`}
                            variant={plan.featured ? 'primary' : 'light'}
                            className='mt-6 w-full'
                        >
                            {plan.cta}
                        </ButtonLink>

                        <ul className='mt-7 space-y-2.5 border-t border-border pt-6'>
                            {plan.features.map(feature => (
                                <li key={feature} className='flex items-start gap-2.5 text-[14px] leading-snug text-foreground/85'>
                                    <Check className='mt-0.5 size-4 shrink-0 text-leaf' />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </article>
                </div>
            ))}
        </div>
    )
}
